import * as React from "react";
import { observer, MobXProviderContext, inject } from "mobx-react";
import { UitkTextInput, UitkFauxInput } from "uitk-react-input";
import { TravelersDialog } from "./TravelersDialog";
import { TravelersHiddenInputs } from "./TravelersHiddenInputs";
import { TravelersFieldProps, Travelers, TravelersTriggerTypes } from "../typings";
import { travelersSummary } from "../utils";
import { RouteStore, QueryRoute } from "bernie-core";
import { ToolbarType } from "uitk-react-toolbar";
import { Layer } from "bernie-view";
import { useLocalization } from "@shared-ui/localization-context";
import { FlightWizardState } from "src/stores/wizard/state";
import { InfantSeating } from "src/components/shared/TravelersField";
import { ACTION_CLOSE, ACTION_DONE, ACTION_OPEN, getTracker } from "utils/TrackingUtils";
import { UitkLink, UitkLinkAlign } from "uitk-react-link";
import { UitkIcon } from "uitk-react-icons";
import { PositionType } from "uitk-react-menu";
import { UitkExperimentalAdaptive } from "uitk-react-experimental-adaptive";

export const TravelersField: React.FunctionComponent<TravelersFieldProps> = inject("analytics")(
  observer((props) => {
    const { triggerType = TravelersTriggerTypes.FAKE_INPUT, handleMenuTriggerState } = props;
    const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
    const inputRef = React.useRef<any | null>(null);
    // we need a unique queryRouteId because the dialog was taking the first one if we repeat the id
    const queryRouteId = `travelers-dialog-${props.lobState.config.elementId}`;
    const {
      numOfAdults,
      numOfChildren,
      numOfInfants,
      flightSearch,
      hotelSearch,
      hotwireHotelSearch,
      packageSearch,
      cruiseSearch,
      groundTransportSearch,
      thirdSearch,
    } = props.metadata;

    const { withFlight, withRooms, withInfants } = props.config;

    const numOfTravelers =
      props.noRehydratationValues?.numOfTravelers && !numOfAdults
        ? props.noRehydratationValues?.numOfTravelers
        : numOfAdults + numOfChildren + (withInfants ? numOfInfants : 0);
    const [showAdaptive, setAdaptive] = React.useState(false);
    const [name, setName] = React.useState<string | undefined>(props.noRehydratationValues?.name);
    const [useHiddenInputs, setUseHiddenInputs] = React.useState(false);
    const [inputClasses, setInputClasses] = React.useState("");
    const [fauxInputClasses, setFauxInputClasses] = React.useState("guests-hidden");
    const summary = travelersSummary(props.config, props.travelers.rooms, numOfTravelers);
    const { formatText } = useLocalization();
    /**
     * This hook is reached for rehydratation, so if JS is enabled then initial values are set here, if JS is disabled then this hook is never executed
     */
    React.useEffect(() => {
      setName(undefined);
      setInputClasses("is-hidden");
      setFauxInputClasses("");
      setUseHiddenInputs(true);

      if (!props.isChanged) {
        const defaultAdultsHotelPackage = 2;
        const defaultAdutlsNonHotelPackage = props.lobState instanceof FlightWizardState ? 1 : 2;
        const adults =
          (withRooms && withFlight) || (props.tripLobType && withRooms) || (!withRooms && !withFlight)
            ? defaultAdultsHotelPackage
            : defaultAdutlsNonHotelPackage;
        const initializedTravelers: Travelers = {
          rooms: [
            {
              adults,
              children: [],
              infants: [],
            },
          ],
          infantSeating: InfantSeating.ON_LAP,
        };

        if (props.onSave) {
          props.onSave(initializedTravelers);
        }
      }
      if (handleMenuTriggerState && showAdaptive) {
        handleMenuTriggerState(false);
      }
    }, [showAdaptive]);

    const onClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      routeStore.updateSearch({ pwaDialog: queryRouteId });

      tracker.trackEvent(_guests + ACTION_OPEN);

      setAdaptive(!showAdaptive);
    };

    const handleClick = () => {
      tracker.trackEvent(_guests + ACTION_OPEN);

      setAdaptive(!showAdaptive);
    };

    const renderAdaptiveDialog = (children: JSX.Element) => {
      return (
        <QueryRoute query={{ pwaDialog: queryRouteId }}>
          <Layer id={`${queryRouteId}-dialog`}>{children}</Layer>
        </QueryRoute>
      );
    };

    const adaptiveTrigger = () => {
      return (
        <>
          {triggerType === TravelersTriggerTypes.FAKE_INPUT && (
            <UitkTextInput
              icon={props.config.icon}
              className={inputClasses}
              label={formatText(props.config.labelToken)}
              value={summary}
              onChange={dummyOnChange}
              name={name}
              data-testid="travelers-field"
              role="button"
            >
              <UitkFauxInput
                onClick={onClick}
                domRef={inputRef}
                className={fauxInputClasses}
                data-testid="travelers-field-trigger"
              >
                {summary}
              </UitkFauxInput>
            </UitkTextInput>
          )}

          {triggerType === TravelersTriggerTypes.LINK_BUTTON && (
            <UitkLink iconPos={UitkLinkAlign.RIGHT} onClick={onClick}>
              <a
                href=""
                data-testid="travelers-field"
                role="button"
                ref={inputRef}
                aria-label={formatText(props.config.labelToken)}
              >
                {summary}
                <UitkIcon name="expand_more" />
              </a>
            </UitkLink>
          )}
        </>
      );
    };

    const onDismiss = () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      routeStore.goBack();
      tracker.trackEvent(_guests + ACTION_CLOSE);

      setAdaptive(false);
    };

    const onSave = (travelers: Travelers) => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
      if (props.onSave) {
        if (props.setChange && !props.isChanged) {
          props.setChange();
        }
        props.onSave(travelers);
      }
      setAdaptive(!showAdaptive);
      tracker.trackEvent(_guests + ACTION_DONE);
    };
    const tracker = getTracker(props.lobState.trackingEnabled, props.analytics, props.lobState.moduleName);
    const _guests = "guests.";

    // error thrown if no onChange handler is passed
    const dummyOnChange = () => null;

    const lobId = props.lobState.config.elementId || "";
    const lobAlignRight = lobId.includes("flight") || lobId.includes("hotel");
    const menuPosition = lobAlignRight ? PositionType.RIGHT : PositionType.LEFT;

    return (
      <>
        <UitkExperimentalAdaptive
          showAdaptive={showAdaptive}
          dialogWrapper={renderAdaptiveDialog}
          handleDialogClick={onClick}
          handleMenuClick={handleClick}
          adaptiveTrigger={adaptiveTrigger}
          menuPosition={menuPosition}
          toolbar={{
            iconLabel: formatText(props.config.dialogCancelLabelToken),
            header: formatText(props.config.dialogTitleToken),
            type: ToolbarType.CLOSE,
            action: onDismiss,
          }}
        >
          <TravelersDialog
            queryRouteId={queryRouteId}
            travelers={props.travelers}
            onDismiss={onDismiss}
            config={props.config}
            onSave={onSave}
            onChange={props.onSave}
            lobState={props.lobState}
            invalid={props.invalid}
          />
        </UitkExperimentalAdaptive>

        {useHiddenInputs && (
          <TravelersHiddenInputs
            hiddenInputType={props.config.hiddenInputType}
            flightSearch={flightSearch}
            hotelSearch={hotelSearch}
            hotwireHotelSearch={hotwireHotelSearch}
            groundTransportSearch={groundTransportSearch}
            packageSearch={packageSearch}
            cruiseSearch={cruiseSearch}
            thirdSearch={thirdSearch}
            isApiCallEnabled={props.isApiCallEnabled}
          />
        )}
      </>
    );
  })
);
